import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';

import { PersonneMoraleProfileData } from './personneMoraleProfile';

export interface ProfileStat {
  label: string;
  value?: React.ReactNode;
}

interface PersonneMoraleProfileHeaderProps {
  profile: PersonneMoraleProfileData;
  /** Label above the name ("Personne morale gérante", "Intermédiaire"…). */
  overline: string;
  /** Extra chips next to the nature chips (e.g. "Administrée par l'admin"). */
  badges?: React.ReactNode;
  /** Paragraph under the name (governance status, description…). */
  description?: React.ReactNode;
  /** Buttons on the right of the hero (Modifier, Déléguer…). None = read-only. */
  actions?: React.ReactNode;
  /** Key figures band; when omitted, ORIAS + SIREN from the profile. */
  stats?: ProfileStat[];
}

const Stat: React.FC<ProfileStat> = ({ label, value }) => (
  <div className="flex flex-1 flex-col gap-0.5 px-6 first:pl-0">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <span className="text-base font-semibold text-gray-900">
      {value ?? '—'}
    </span>
  </div>
);

/**
 * Hero of a personne morale profile: initials avatar, overline, name + nature
 * chips, description and key-figures band. Purely presentational — governance
 * plugs its delegation logic through `description`/`actions`, the intermediary
 * quick view uses it without actions.
 */
export const PersonneMoraleProfileHeader: React.FC<
  PersonneMoraleProfileHeaderProps
> = ({ profile, overline, badges, description, actions, stats }) => {
  const initials = profile.name
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const effectiveStats: ProfileStat[] = stats ?? [
    { label: 'ORIAS', value: profile.orias },
    { label: sirenOrSiretLabel(profile.siren ?? ''), value: profile.siren }
  ];

  return (
    <div className="pm-profile-hero rounded-xl bg-white p-6">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
            {overline}
          </span>
          <div className="flex items-center gap-2">
            <p className="truncate text-lg font-semibold text-gray-900">
              {profile.name}
            </p>
            {profile.natureLabel && (
              <span className="flex-shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                {profile.natureLabel}
              </span>
            )}
            {profile.sousNatureLabel && (
              <span className="flex-shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                {profile.sousNatureLabel}
              </span>
            )}
            {badges}
          </div>
          {description}
        </div>
        {actions && (
          <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>

      <div className="mt-4 flex w-full divide-x divide-gray-200 border-t border-gray-200 pt-4">
        {effectiveStats.map(stat => (
          <Stat key={stat.label} label={stat.label} value={stat.value} />
        ))}
      </div>
    </div>
  );
};
